import React, { useMemo } from "react";
import { Select } from "antd";
import { ProductTypeDto } from "apiClients/useProductTypeApiClient";
import { useProductTypesQuery } from "../hooks/useProductTypesQuery";
import { compareProductTypes } from "../productTypeTree";

type ProductTypeSelectProps = {
  value?: number | null;
  onChange?: (value: number | null) => void;
  allowClear?: boolean;
  placeholder?: string;
  style?: React.CSSProperties;
  disabled?: boolean;
};

const buildPathLabel = (
  productType: ProductTypeDto,
  byId: Map<number, ProductTypeDto>
): string => {
  const parts: string[] = [];
  let current: ProductTypeDto | undefined = productType;

  while (current) {
    parts.unshift(current.name || `Typ #${current.id}`);
    current = current.parentProductTypeId
      ? byId.get(current.parentProductTypeId)
      : undefined;
  }

  return parts.join(" › ");
};

/**
 * Searchable flat select for assigning a product type (Produktöversikt / forms).
 * Labels show the full path so parents vs children stay clear.
 */
const ProductTypeSelect: React.FC<ProductTypeSelectProps> = ({
  value,
  onChange,
  allowClear = true,
  placeholder = "Välj produkttyp",
  style,
  disabled,
}) => {
  const { data: productTypes = [], isLoading } = useProductTypesQuery();

  const options = useMemo(() => {
    const byId = new Map(productTypes.map((pt) => [pt.id, pt]));
    const byParent = new Map<number | null, ProductTypeDto[]>();
    productTypes.forEach((pt) => {
      const parentKey = pt.parentProductTypeId ?? null;
      const list = byParent.get(parentKey) ?? [];
      list.push(pt);
      byParent.set(parentKey, list);
    });
    byParent.forEach((list, key) => byParent.set(key, list.slice().sort(compareProductTypes)));

    const ordered: ProductTypeDto[] = [];
    const visit = (parentId: number | null) => {
      (byParent.get(parentId) ?? []).forEach((pt) => {
        ordered.push(pt);
        visit(pt.id);
      });
    };
    visit(null);

    return ordered.map((productType) => ({
      value: productType.id,
      label: buildPathLabel(productType, byId),
    }));
  }, [productTypes]);

  return (
    <Select
      style={{ width: "100%", ...style }}
      value={value ?? undefined}
      onChange={(next) => onChange?.(next ?? null)}
      options={options}
      loading={isLoading}
      allowClear={allowClear}
      placeholder={placeholder}
      showSearch
      optionFilterProp="label"
      disabled={disabled}
    />
  );
};

export default ProductTypeSelect;
